import React, { type PropsWithChildren, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import invariant from 'tiny-invariant';

import {
	attachInstruction,
	extractInstruction,
	type Instruction,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/tree-item';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { getMaximumNestingLevelLimit } from '../../../../../controllers/use-links-menu-parameters-and-actions';
import { ITEM, type ItemPositionalProps, type ItemType } from '../../../../../types';

const draggingStyles = xcss({
	opacity: 0.5,
});
const wrapperStyles = xcss({
	position: 'relative',
	cursor: 'grab',
});

const previewStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	borderRadius: 'radius.small',
	backgroundColor: 'elevation.surface.overlay',
	maxWidth: '360px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

export type DraggableLinksMenuItemData = Pick<
	DraggableLinksMenuItemContainerProps,
	'item' | 'index' | 'parentId'
> & {
	id: string;
	type: ITEM;
};

export type DraggableLinksMenuItemContainerProps = Pick<ItemPositionalProps, 'index' | 'level'> &
	PropsWithChildren<{
		isDragging: boolean;
		item: ItemType;
		parentId?: string;
		setIsDragging: (dragging: boolean) => void;
	}>;

export const DraggableLinksMenuItemContainer = ({
	children,
	item,
	isDragging,
	setIsDragging,
	index,
	parentId,
	level,
}: DraggableLinksMenuItemContainerProps): React.JSX.Element => {
	const ref = useRef<HTMLDivElement>(null);

	const [dropIndicatorInstruction, setDropIndicatorInstruction] = useState<Instruction | null>();
	const [previewContainer, setPreviewContainer] = useState<Element | DocumentFragment | null>();

	useEffect(() => {
		const el = ref.current;
		invariant(el);

		return combine(
			draggable({
				element: el,
				onDragStart: () => setIsDragging(true),
				onDrop: () => setIsDragging(false),
				getInitialData: () => {
					return {
						id: item.itemId,
						parentId,
						index,
						type: item.type,
						item,
					};
				},
				onGenerateDragPreview: ({ nativeSetDragImage }) => {
					setCustomNativeDragPreview({
						nativeSetDragImage,
						getOffset: pointerOutsideOfPreview({
							x: token('space.200'),
							y: token('space.100'),
						}),
						render: ({ container }) => {
							setPreviewContainer(container);
						},
					});
				},
			}),
			dropTargetForElements({
				element: el,
				canDrop: () => {
					// Stop user from dragging & dropping into deep nesting.
					// It's possible for users to work around our guardrails here and
					// and nest more levels than we "allow," by dragging and dropping already
					// deeply-nested trees into another deep nested target - as long as
					// the target element isn't exceeding the max level.
					// User will only be halted by some restrictions on the item creation buttons
					// and cursory checks like the ones below.
					// These guardrails are in place to 1. help admins make their content
					// accessible / well designed and 2. enforce scale limits.
					// With 1, we can let a user choose to work around that purposely, but they
					// still won't be able to get around our harder item number limit (see
					// MAXIMUM_ITEM_LIMIT) unless they directly query backend with an ADF update,
					// so 2 is not really a concern either.
					if (level > getMaximumNestingLevelLimit() + 1) {
						return false;
					}
					return true;
				},
				onDragLeave: () => {
					setDropIndicatorInstruction(null);
					setPreviewContainer(null);
				},
				onDrop: () => {
					setDropIndicatorInstruction(null);
					setPreviewContainer(null);
				},
				onDrag: ({ self, source }) => {
					const instruction = extractInstruction(self.data);
					if (source.data.id === item.itemId || instruction?.type === 'instruction-blocked') {
						setDropIndicatorInstruction(null);
					} else {
						setDropIndicatorInstruction(instruction);
					}
				},
				getData: ({ input, element }) => {
					const data: DraggableLinksMenuItemData = {
						id: item.itemId,
						parentId,
						index,
						type: item.type,
						item,
					};

					// only menu items can be parents, so block make-child for links
					let blockInstructions: Instruction['type'][] = ['make-child'];
					if (item.type === ITEM.MENU) {
						blockInstructions = [];
						// Don't let user reorder-below a menu item that has children,
						// because the drop indicator shows up under the menu item and
						// doesn't visually represent the end state (current item will
						// reorder *after* the menu *plus* all its children).
						// User will still be able to achieve their desired goal by
						// reordering above the subsequent item on the same level as
						// this menu item.
						// Exception is if this menu item is last in the list. In that case,
						// they'll need to use a workaround for now by dragging the menu item
						// upwards instead. @TODO, fix this papercut.
						if (item.childItems?.length) {
							blockInstructions.push('reorder-below');
						}
						if (level > getMaximumNestingLevelLimit()) {
							blockInstructions.push('make-child');
						}
					}

					return attachInstruction(data, {
						input,
						element,
						currentLevel: level,
						indentPerLevel: 0,
						mode: 'standard',
						block: blockInstructions,
					});
				},
			}),
		);
	}, [item, setIsDragging, parentId, index, setDropIndicatorInstruction, level]);

	return (
		<>
			<Box
				ref={ref}
				xcss={[isDragging && draggingStyles, wrapperStyles]}
				testId={`draggable-links-menu-item-${item.itemId}`}
			>
				{children}
				{dropIndicatorInstruction ? <DropIndicator instruction={dropIndicatorInstruction} /> : null}
			</Box>
			{previewContainer &&
				createPortal(<Box xcss={previewStyles}>{item.title}</Box>, previewContainer)}
		</>
	);
};
